import { Button } from "@/components/Button"
import { TablePagination } from "@/components/Pagination/TablePagination"
import { Autocomplete } from "@/components/Selector/Autocomplete"
import { ITableHead, Table } from "@/components/Table"
import { DownloadIcon } from "@/components/two-factor/DownloadIcon"
import { IBANApi, IBANContext, useIBANStore, useIBANTransactions, utils } from "@usher/pe-client-front-end-lib"
import { clsx } from "clsx"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { PaymentsTableFilter } from "./Filter"
import { resolveStatus } from "./resolveStatus"
import { StatusFilter } from "./StatusFilter"
import "./style.sass"
import { TYPE_OPTIONS } from "./table-heads"
import { TransactionInfo } from "./TransactionInfo"

export const IBANTransactionTable = () => {
  const { currencyFilter } = useContext(IBANContext)
  const iban = useIBANStore((state) => state.iban)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null])

  const { transactions, loading, status_id, setStatus_id, total, pagination, setPagination, type, setType, filter, setFilter } = useIBANTransactions()
  const onResultClick = useCallback(() => {
    setFilter({
      ...filter,
      // @ts-ignore
      time_from: range?.[0] ? utils.StringUtils.getDateFilterString(range[0]) : void 0,
      // @ts-ignore
      time_to: range?.[1] ? utils.StringUtils.getDateFilterString(range[1]) : void 0,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, filter])

  const downloadStatement = useCallback(async () => {
    setDownloadLoading(true)
    try {
      await IBANApi.downloadRequisites(iban, void 0, {
        ...filter,
        // @ts-ignore
        time_from: range?.[0] ? utils.StringUtils.getDateFilterString(range[0]) : void 0,
        // @ts-ignore
        time_to: range?.[1] ? utils.StringUtils.getDateFilterString(range[1]) : void 0,
      })
    } finally {
      setDownloadLoading(false)
    }
  }, [iban, range, filter])
  useEffect(() => {
    setStatus_id({ label: "All", value: "0" })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyFilter])

  const tableHeads = useMemo((): ITableHead[] => {
    return [
      { label: "ID", key: "id" },
      { label: "Created At", key: "date_created" },
      { label: "Sender/Recepient", key: "sender_recipient" },
      { label: "Account #", key: "receiver_iban" },
      { label: "Amount", key: "amount" },
      { label: "Fee", key: "total_fee" },
      { label: "Total Credit/Debit", key: "total" },
      {
        // @ts-ignore
        label: (
          <Autocomplete
            styles={{
              menu: (base) => ({ ...base, minWidth: 200 }),
              container: (base) => ({ ...base, minWidth: 200 }),
            }}
            value={type}
            //@ts-ignore
            onChange={(val) => setType(val ?? void 0)}
            label="Type"
            options={TYPE_OPTIONS}
            size="sm"
          />
        ),
        key: "transaction_type",
      },
      { label: <StatusFilter onChange={setStatus_id} value={status_id} />, key: "status" },
      { label: "Details", key: "action" },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, status_id])

  const tableData = useMemo(() => {
    return transactions.map((transaction) => {
      const isIncoming = transaction.transaction_type.toLowerCase().includes("incoming")
      const isTopup = transaction.transaction_type.toLowerCase().includes("top up")
      const senderInfo = (
        <>
          <p>{transaction.sender_name ?? ""}</p>
          {transaction.sender_iban != null && <p>{transaction.sender_iban}</p>}
        </>
      )
      const receiverInfo = (
        <>
          <p>{transaction.receiver_name ?? ""}</p>
          {transaction.receiver_iban != null && <p>{transaction.receiver_iban}</p>}
        </>
      )
      const hasFee = transaction.total_fee != null && Number(transaction.total_fee) > 0
      return {
        ...transaction,
        date_created: (
          <div className="flex flex-col">
            {transaction.date_created.split(" ").map((chunk) => (
              <span>{chunk}</span>
            ))}
          </div>
        ),
        sender_recipient: isIncoming || isTopup ? senderInfo : receiverInfo,
        id: `# ${transaction.id}`,
        receiver_iban: isIncoming || isTopup ? receiverInfo : senderInfo,
        amount: (
          <div>
            {/* @ts-ignore*/}
            {Number(transaction.amount ?? 0).toFixed(2)} {transaction.currency}
          </div>
        ),

        total_fee: (
          <div className={clsx(hasFee ? "text-red-600" : "inherit")}>
            {/* @ts-ignore*/}
            {Number(transaction.total_fee ?? 0).toFixed(2)} {transaction.currency}
          </div>
        ),
        status: <div className={resolveStatus(transaction.status)}>{transaction.status}</div>,
        total: (
          <div className={clsx(isIncoming || isTopup ? "text-green-600" : "text-red-600")}>
            {Number(Number(transaction.amount ?? 0) + (isIncoming ? -Number(transaction.total_fee) : Number(transaction.total_fee))).toFixed(2)}{" "}
            {transaction.currency}
          </div>
        ),
        action: <TransactionInfo transaction={transaction} />,
      }
    })
  }, [transactions])
  return (
    <div className="transaction-table mt-5">
      <div className="bg-white border">
        <PaymentsTableFilter {...{ filter, setFilter, onResultClick, range, setRange }} onDownload={downloadStatement} loading={downloadLoading} />
        <div className="pl-4 mr-4">
          <Table loading={loading} heads={tableHeads} data={tableData} />
        </div>
      </div>
      {/*           <Button onClick={() => IBANApi.downloadRequisites(iban)}> */}
      <TablePagination
        leftSection={
          <Button loading={downloadLoading} onClick={downloadStatement}>
            <DownloadIcon /> Download
          </Button>
        }
        perPage={total < pagination.count ? total : (pagination.count ?? 5)}
        currentPage={pagination.page ?? 1}
        totalPages={Math.ceil(total / (pagination.count ?? 5))}
        dataTotal={total}
        onPageChange={(page) => setPagination({ ...pagination, page })}
        className="mt-3"
      />
    </div>
  )
}
